import { useEffect, useState } from "react";
import { vpAxios, money, downloadPdf } from "../api";
import { toast } from "sonner";
import { Plus, TrendingUp, TrendingDown, FileText, Calendar, BarChart2, ListOrdered } from "lucide-react";

const hojeISO = () => new Date().toISOString().slice(0, 10);
const yesterdayISO = (d = 1) => {
  const t = new Date(); t.setDate(t.getDate() - d);
  return t.toISOString().slice(0, 10);
};

export default function CaixaPage() {
  const [resumo, setResumo] = useState(null);
  const [movs, setMovs] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("hoje"); // hoje | detalhado | resumido
  const [de, setDe] = useState(yesterdayISO(7));
  const [ate, setAte] = useState(hojeISO());
  const [rel, setRel] = useState(null);
  const [loadingRel, setLoadingRel] = useState(false);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      vpAxios.get("/vaipede/caixa/resumo"),
      vpAxios.get("/vaipede/caixa/movimentacoes"),
    ]);
    setResumo(r1.data); setMovs(r2.data);
  };
  useEffect(() => { load(); }, []);

  const loadRel = async (tipo) => {
    setLoadingRel(true);
    try {
      const { data } = await vpAxios.get(`/vaipede/caixa/relatorio/${tipo}`, { params: { de, ate } });
      setRel(data);
    } catch (e) { toast.error("Erro ao gerar relatório"); }
    finally { setLoadingRel(false); }
  };
  useEffect(() => {
    if (tab === "detalhado") loadRel("detalhado");
    if (tab === "resumido") loadRel("resumido");
    // eslint-disable-next-line
  }, [tab, de, ate]);

  const baixarPDF = (tipo) => {
    const params = new URLSearchParams({ tipo, de, ate }).toString();
    downloadPdf(`/vaipede/caixa/relatorio/pdf?${params}`, `relatorio-${tipo}-${de}-${ate}.pdf`)
      .catch(() => toast.error("Erro no PDF"));
  };

  const add = async (d) => {
    try { await vpAxios.post("/vaipede/caixa/movimentacao", d); toast.success("Registrado"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  if (!resumo) return <div>Carregando...</div>;

  return (
    <div data-testid="vaipede-caixa">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black">Caixa & Relatórios</h1>
          <p className="text-sm text-neutral-500">Diário e por período</p>
        </div>
        <button onClick={() => setOpen(true)} className="px-4 py-2.5 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm flex items-center gap-2" data-testid="vaipede-caixa-nova-mov">
          <Plus className="w-4 h-4"/> Nova movimentação
        </button>
      </div>

      <div className="flex gap-2 mb-5 border-b border-neutral-200 overflow-auto">
        {[{k:"hoje",l:"Diário (hoje)",i:<Calendar className="w-4 h-4"/>},
          {k:"detalhado",l:"Detalhado por período",i:<ListOrdered className="w-4 h-4"/>},
          {k:"resumido",l:"Resumido por período",i:<BarChart2 className="w-4 h-4"/>}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 -mb-px border-b-2 flex items-center gap-1.5 whitespace-nowrap text-sm font-bold ${tab === t.k ? "border-[#EA1D2C] text-[#EA1D2C]" : "border-transparent text-neutral-500"}`}
            data-testid={`vaipede-caixa-tab-${t.k}`}>
            {t.i} {t.l}
          </button>
        ))}
      </div>

      {tab === "hoje" && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="text-xs text-neutral-500">Saldo inicial</div>
              <div className="text-2xl font-black mt-1">{money(resumo.saldo_inicial)}</div>
            </div>
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="text-xs text-neutral-500">Entradas</div>
              <div className="text-2xl font-black mt-1 text-emerald-600">{money(resumo.entradas)}</div>
            </div>
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="text-xs text-neutral-500">Saídas</div>
              <div className="text-2xl font-black mt-1 text-red-600">{money(resumo.saidas)}</div>
            </div>
            <div className="bg-neutral-900 rounded-2xl p-5 text-white">
              <div className="text-xs text-neutral-400">Saldo atual</div>
              <div className="text-2xl font-black mt-1">{money(resumo.saldo_atual)}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <h2 className="font-bold mb-3">Formas de pagamento</h2>
              {resumo.formas_pagamento.length === 0 && <p className="text-sm text-neutral-500">Sem entradas hoje.</p>}
              <div className="space-y-2">
                {resumo.formas_pagamento.map(f => (
                  <div key={f.forma} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{f.forma.replace("_", " ")}</span>
                    <b>{money(f.total)}</b>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold">Movimentações do dia</h2>
                <button onClick={() => { setDe(hojeISO()); setAte(hojeISO()); baixarPDF("detalhado"); }} className="text-xs text-[#EA1D2C] font-bold flex items-center gap-1" data-testid="vaipede-caixa-pdf-diario">
                  <FileText className="w-3 h-3"/> PDF
                </button>
              </div>
              {movs.length === 0 && <p className="text-sm text-neutral-500">Sem movimentações.</p>}
              <div className="space-y-2 max-h-72 overflow-auto">
                {movs.map(m => (
                  <div key={m.id} className="flex items-center gap-2 py-2 border-b border-neutral-100 text-sm">
                    {m.tipo === "entrada" ? <TrendingUp className="w-4 h-4 text-emerald-600"/> : <TrendingDown className="w-4 h-4 text-red-600"/>}
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{m.descricao || m.origem}</div>
                      <div className="text-xs text-neutral-500 capitalize">{m.forma}</div>
                    </div>
                    <b className={m.tipo === "entrada" ? "text-emerald-600" : "text-red-600"}>{m.tipo === "entrada" ? "+" : "-"}{money(m.valor)}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {(tab === "detalhado" || tab === "resumido") && (
        <>
          <div className="bg-white rounded-2xl border border-neutral-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-[11px] font-bold text-neutral-500 block mb-1">De</label>
              <input type="date" value={de} onChange={e => setDe(e.target.value)} className="px-3 py-2 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-caixa-de"/>
            </div>
            <div>
              <label className="text-[11px] font-bold text-neutral-500 block mb-1">Até</label>
              <input type="date" value={ate} onChange={e => setAte(e.target.value)} className="px-3 py-2 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-caixa-ate"/>
            </div>
            <button onClick={() => baixarPDF(tab)} className="ml-auto px-4 py-2 rounded-xl bg-neutral-900 text-white font-bold text-sm flex items-center gap-2" data-testid={`vaipede-caixa-pdf-${tab}`}>
              <FileText className="w-4 h-4"/> Baixar PDF
            </button>
          </div>

          {loadingRel && <div className="text-sm text-neutral-500">Carregando relatório...</div>}
          {rel && tab === "detalhado" && (
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div><div className="text-xs text-neutral-500">Entradas</div><div className="font-black text-lg text-emerald-600">{money(rel.totais.entradas)}</div></div>
                <div><div className="text-xs text-neutral-500">Saídas</div><div className="font-black text-lg text-red-600">{money(rel.totais.saidas)}</div></div>
                <div><div className="text-xs text-neutral-500">Saldo</div><div className="font-black text-lg">{money(rel.totais.saldo)}</div></div>
              </div>
              <h2 className="font-bold mb-2">{rel.totais.qtd} movimentações</h2>
              <div className="space-y-1 max-h-[60vh] overflow-auto text-sm">
                {rel.movimentacoes.map(m => (
                  <div key={m.id} className="flex items-center gap-2 py-1.5 border-b border-neutral-100">
                    {m.tipo === "entrada" ? <TrendingUp className="w-4 h-4 text-emerald-600"/> : <TrendingDown className="w-4 h-4 text-red-600"/>}
                    <span className="text-xs text-neutral-500 tabular-nums">{new Date(m.data).toLocaleString("pt-BR", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"})}</span>
                    <span className="truncate flex-1">{m.descricao || m.origem}</span>
                    <span className="text-xs text-neutral-500 capitalize">{m.forma}</span>
                    <b className={m.tipo === "entrada" ? "text-emerald-600" : "text-red-600"}>{m.tipo === "entrada" ? "+" : "-"}{money(m.valor)}</b>
                  </div>
                ))}
                {rel.movimentacoes.length === 0 && <div className="text-neutral-500 text-sm">Sem movimentações no período.</div>}
              </div>
            </div>
          )}

          {rel && tab === "resumido" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-neutral-200 p-5">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div><div className="text-xs text-neutral-500">Faturamento</div><div className="font-black text-lg text-emerald-600">{money(rel.totais.faturamento)}</div></div>
                  <div><div className="text-xs text-neutral-500">Pedidos</div><div className="font-black text-lg">{rel.totais.pedidos}</div></div>
                  <div><div className="text-xs text-neutral-500">Itens vendidos</div><div className="font-black text-lg">{rel.totais.itens_vendidos}</div></div>
                  <div><div className="text-xs text-neutral-500">Ticket médio</div><div className="font-black text-lg">{money(rel.totais.ticket_medio)}</div></div>
                </div>
                <h2 className="font-bold mb-2">Por forma de pagamento</h2>
                <div className="space-y-1">
                  {rel.por_forma_pagamento.map(f => (
                    <div key={f.forma} className="flex items-center justify-between text-sm py-1 border-b border-neutral-100">
                      <span className="capitalize">{f.forma.replace("_", " ")}</span>
                      <div className="text-right">
                        <b>{money(f.total)}</b>
                        <div className="text-xs text-neutral-500">{f.qtd} movs</div>
                      </div>
                    </div>
                  ))}
                  {rel.por_forma_pagamento.length === 0 && <div className="text-sm text-neutral-500">Sem entradas.</div>}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-neutral-200 p-5">
                <h2 className="font-bold mb-3">Ranking por produto</h2>
                <div className="space-y-1 max-h-[60vh] overflow-auto">
                  {rel.por_produto.map((p, i) => (
                    <div key={p.nome} className="flex items-center gap-2 py-1.5 border-b border-neutral-100 text-sm">
                      <span className="w-6 text-xs font-black text-neutral-400">{i + 1}º</span>
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-bold">{p.nome}</div>
                        <div className="text-xs text-neutral-500">{p.quantidade}× · {p.categoria || "—"}</div>
                      </div>
                      <b>{money(p.total)}</b>
                    </div>
                  ))}
                  {rel.por_produto.length === 0 && <div className="text-sm text-neutral-500">Sem vendas.</div>}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {open && <MovModal onSave={add} onClose={() => setOpen(false)}/>}
    </div>
  );
}

function MovModal({ onSave, onClose }) {
  const [f, setF] = useState({ tipo: "saida", valor: "", forma: "dinheiro", descricao: "" });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5">
        <h2 className="font-black text-lg mb-4">Nova movimentação</h2>
        <div className="space-y-2">
          <select value={f.tipo} onChange={e => setF({...f, tipo: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm">
            <option value="entrada">Entrada</option>
            <option value="saida">Saída</option>
          </select>
          <input type="number" step="0.01" value={f.valor} onChange={e => setF({...f, valor: e.target.value})} placeholder="Valor" className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
          <select value={f.forma} onChange={e => setF({...f, forma: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm">
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">Pix</option>
            <option value="cartao_credito">Cartão de crédito</option>
            <option value="cartao_debito">Cartão de débito</option>
            <option value="outros">Outros</option>
          </select>
          <input value={f.descricao} onChange={e => setF({...f, descricao: e.target.value})} placeholder="Descrição" className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-neutral-100 font-bold text-sm">Cancelar</button>
          <button onClick={() => onSave({...f, valor: parseFloat(f.valor || 0)})} className="flex-1 py-2.5 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm">Salvar</button>
        </div>
      </div>
    </div>
  );
}
